import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export interface FrontendLogEntry {
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  context?: string;
  ts?: string;
  path?: string;
  // room for extra metadata in future (e.g. userId)
  [k: string]: any;
}

@Injectable({ providedIn: 'root' })
export class FrontendLoggerService {
  private readonly apiUrl = environment.apiUrl + '/rag/frontend-log';
  private readonly isProduction = environment.production === true;

  constructor() { }

  private fireAndForget(entry: FrontendLogEntry) {
    try {
      void this.forwardEntry(this.sanitizeEntry(entry));
    } catch (e) {
      // Last resort to avoid throwing inside logging path
      // eslint-disable-next-line no-console
      console.error('[FrontendLogger] Falló envío de log', e);
    }
  }

  private sanitizeEntry(entry: FrontendLogEntry): FrontendLogEntry {
    return {
      ...entry,
      message: this.redactSensitiveText(entry.message || ''),
      context: this.sanitizeUnknown(entry.context),
    };
  }

  private sanitizeUnknown(value: unknown): string | undefined {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    if (typeof value === 'string') {
      return this.redactSensitiveText(value);
    }

    return this.redactSensitiveText(this.safeStringify(this.redactObject(value)));
  }

  private safeStringify(value: unknown): string {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private redactObject(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.redactObject(item));
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: this.redactSensitiveText(value.message),
      };
    }

    if (typeof value !== 'object' || value === null) {
      return value;
    }

    return Object.entries(value).reduce<Record<string, unknown>>((acc, [key, item]) => {
      if (this.isSensitiveKey(key)) {
        acc[key] = '[REDACTED]';
        return acc;
      }

      acc[key] = this.redactObject(item);
      return acc;
    }, {});
  }

  private isSensitiveKey(key: string): boolean {
    return /(authorization|token|refresh.?token|access.?token|password|secret|code|state|email)/i.test(key);
  }

  private redactSensitiveText(value: string): string {
    return value
      .replaceAll(/(authorization\s*:\s*bearer\s+)[^\s"']+/gi, '$1[REDACTED]')
      .replaceAll(/([?&](?:code|state|access_token|refresh_token|token)=)[^&\s]+/gi, '$1[REDACTED]')
      .replaceAll(/("(?:code|state|access_token|refresh_token|token|password|secret|email)"\s*:\s*")[^"]*(")/gi, '$1[REDACTED]$2');
  }

  private async forwardEntry(entry: FrontendLogEntry): Promise<void> {
    try {
      await this.sendLog(entry);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[FrontendLogger] Error enviando log al backend', {
        message: entry.message,
        path: entry.path,
        level: entry.level,
        error: String(error)
      });
    }
  }

  private async sendLog(payload: FrontendLogEntry): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!response.ok) {
      throw new Error(`Frontend log failed with status ${response.status}`);
    }
  }

  log(entry: FrontendLogEntry) {
    const enriched: FrontendLogEntry = {
      ts: new Date().toISOString(),
      path: globalThis.window === undefined ? undefined : globalThis.window.location.pathname,
      ...entry,
    };
    this.fireAndForget(enriched);
  }

  info(message: string, context?: unknown) {
    const safeMessage = this.redactSensitiveText(message);
    const safeContext = this.sanitizeUnknown(context);
    if (!this.isProduction) {
      console.info(`[FrontendLogger] ${safeMessage}`, safeContext);
    }
    this.log({ level: 'info', message: safeMessage, context: safeContext });
  }

  warn(message: string, context?: unknown) {
    const safeMessage = this.redactSensitiveText(message);
    const safeContext = this.sanitizeUnknown(context);
    console.warn(`[FrontendLogger] ${safeMessage}`, safeContext);
    this.log({ level: 'warn', message: safeMessage, context: safeContext });
  }

  error(message: string, context?: unknown) {
    const safeMessage = this.redactSensitiveText(message);
    const safeContext = this.sanitizeUnknown(context);
    console.error(`[FrontendLogger] ${safeMessage}`, safeContext);
    this.log({ level: 'error', message: safeMessage, context: safeContext });
  }

  debug(message: string, context?: unknown) {
    const safeMessage = this.redactSensitiveText(message);
    const safeContext = this.sanitizeUnknown(context);
    if (!this.isProduction) {
      console.debug(`[FrontendLogger] ${safeMessage}`, safeContext);
    }
    this.log({ level: 'debug', message: safeMessage, context: safeContext });
  }

}
